import { Box, Button, CircularProgress, Stack, Switch, Typography } from "@mui/material";
import { PinnacleTextField } from "../../../components/PinnacleTextField";
import { E_FIELD_REQUIRED } from "../../../utils/constants";
import { Program } from "../../../api/interfaces/Program";
import { FormData } from "../hooks/useProgramForm";
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';

interface EditProgramDrawerContentProps {
  formData: FormData;
  touchedFields: Record<keyof FormData, boolean>;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (field: keyof FormData) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  programs: Program[];
  isFormValid: () => boolean;
  handleUploadClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedImage: File | null;
  handleUploadBannerClick: () => void;
  bannerFileInputRef: React.RefObject<HTMLInputElement>;
  handleBannerFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  selectedBannerImage: File | null;
  isFormModified: boolean;
}

const EditProgramDrawerContent: React.FC<EditProgramDrawerContentProps> = ({
  formData,
  touchedFields,
  onChange,
  onBlur,
  onSubmit,
  isSubmitting,
  programs,
  isFormValid,
  handleUploadClick,
  fileInputRef,
  handleFileChange,
  selectedImage,
  handleUploadBannerClick,
  bannerFileInputRef,
  handleBannerFileChange,
  selectedBannerImage,
  isFormModified
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PinnacleTextField
        label="Name"
        name="name"
        value={formData.name}
        onChange={onChange}
        fullWidth
        onBlur={() => onBlur('name')}
        error={touchedFields.name && !formData.name.trim()}
        helperText={
          touchedFields.name && !formData.name.trim()
            ? E_FIELD_REQUIRED
            : ' '
        }
        disabled={isSubmitting}
      />

      <PinnacleTextField
        label="Code"
        name="code"
        value={formData.code}
        onChange={onChange}
        multiline
        rows={3}
        fullWidth
        onBlur={() => onBlur('code')}
        error={touchedFields.code && !formData.code.trim()}
        helperText={
          touchedFields.code && !formData.code.trim()
            ? E_FIELD_REQUIRED
            : ' '
        }
        disabled={isSubmitting}
      />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%", mb: 3 }}
      >
        <Typography variant="body2" sx={{ color: '#818181' }}>
          Set the program as <Box component="span" sx={{ fontWeight: 'bold' }}>“UNDERGRAD”</Box>?
        </Typography>

        <Switch
          checked={formData.isUndergrad}
          onChange={onChange}
          disabled={isSubmitting}
          name="isUndergrad"
        />
      </Box>

      {/* Cover Photo */}
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        Cover Photo
      </Typography>
      <Stack spacing={2}>
        <Box
          onClick={handleUploadClick}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            p: 2,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f9f9f9',
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {selectedImage ? (
              <>
                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    noWrap
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: 200
                    }}
                  >
                    {selectedImage.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to change cover
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={500}>
                    Upload Cover Photo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse file to upload
                  </Typography>
                </Box>
              </>
            )}
          </Stack>

          <input
            type="file"
            accept="image/jpeg, image/jpg, image/png, image/svg+xml"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ color: '#818181', mb: 1 }}>
            {selectedImage ? "Selected Cover" : "Current Thumbnail"}
          </Typography>
          <Box
            sx={{
              border: '2px solid #ccc',
              borderRadius: '8px',
              p: 1,
              textAlign: 'center',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            }}
          >
            {selectedImage ? (
              <Box
                component="img"
                src={URL.createObjectURL(selectedImage)}
                alt="Selected Cover"
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  borderRadius: 2,
                  alignSelf: 'center',
                }}
              />
            ) : formData.coverPhoto ? (
              <Box
                component="img"
                src={formData.coverPhoto}
                alt={formData.name}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  borderRadius: 2,
                  alignSelf: 'center',
                }}
              />
            ) : null}
          </Box>
        </Box>
      </Stack>

      {/* Banner */}
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        Banner Photo
      </Typography>
      <Stack spacing={2}>
        <Box
          onClick={handleUploadBannerClick}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            p: 2,
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f9f9f9',
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {selectedBannerImage ? (
              <>
                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    noWrap
                    sx={{
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: 200
                    }}
                  >
                    {selectedBannerImage.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click to change banner
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <InsertPhotoIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={500}>
                    Upload Banner Photo
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Browse file to upload
                  </Typography>
                </Box>
              </>
            )}
          </Stack>

          <input
            type="file"
            accept="image/jpeg, image/jpg, image/png, image/svg+xml"
            ref={bannerFileInputRef}
            onChange={handleBannerFileChange}
            style={{ display: 'none' }}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="body1" sx={{ color: '#818181', mb: 1 }}>
            {selectedBannerImage ? "Selected Banner" : "Current Thumbnail"}
          </Typography>
          <Box
            sx={{
              border: '2px solid #ccc',
              borderRadius: '8px',
              p: 1,
              textAlign: 'center',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            }}
          >
            {selectedBannerImage ? (
              <Box
                component="img"
                src={URL.createObjectURL(selectedBannerImage)}
                alt="Selected Cover"
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  borderRadius: 2,
                  alignSelf: 'center',
                }}
              />
            ) : formData.banner ? (
              <Box
                component="img"
                src={formData.banner}
                alt={formData.name}
                sx={{
                  width: '100%',
                  maxWidth: '300px',
                  borderRadius: 2,
                  alignSelf: 'center',
                }}
              />
            ) : null}
          </Box>
        </Box>
      </Stack>

      <Button
        variant="contained"
        color="primary"
        onClick={onSubmit}
        disabled={isSubmitting || !isFormValid() || !isFormModified}
        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
      >
        UPDATE
      </Button>
    </Box>
  );
};

export default EditProgramDrawerContent;